import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { FaArrowRight, FaImage } from 'react-icons/fa';
import './AdminBlogEdit.css';

const AdminBlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const isNew = !id || id === 'new';
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    author: 'د. نهى الحازمي',
    status: 'draft',
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (!isNew && id) {
      axios
        .get(`${API_BASE}/api/admin/blog/${id}`, { headers: getAuthHeaders() })
        .then((res) => {
          const p = res.data.post;
          setForm({
            title: p.title || '',
            excerpt: p.excerpt || '',
            content: p.content || '',
            featured_image: p.featured_image || '',
            author: p.author || 'د. نهى الحازمي',
            status: p.status || 'draft',
          });
        })
        .catch(() => alert('فشل تحميل المنشور'));
    }
  }, [id, isNew]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      const fd = new FormData();
      fd.append('image', file);
      axios
        .post(`${API_BASE}/api/admin/blog/upload`, fd, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => {
          const url = res.data.url;
          handleChange('featured_image', url);
        })
        .catch(() => alert('فشل رفع الصورة'))
        .finally(() => setUploading(false));
    },
    []
  );

  const insertImageInEditor = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('image', file);
      axios
        .post(`${API_BASE}/api/admin/blog/upload`, fd, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => {
          const url = `${API_BASE}${res.data.url}`;
          const editor = quillRef.current?.getEditor?.();
          if (editor) {
            const range = editor.getSelection(true);
            const index = range?.index ?? editor.getLength();
            editor.insertEmbed(index, 'image', url);
            editor.setSelection(index + 1);
          }
        })
        .catch(() => alert('فشل رفع الصورة'));
    };
    input.click();
  }, []);

  const modules = React.useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: { image: insertImageInEditor },
      },
    }),
    [insertImageInEditor]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('العنوان مطلوب');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim() || null,
      content: form.content,
      featured_image: form.featured_image || null,
      author: form.author.trim() || 'د. نهى الحازمي',
      status: form.status,
    };
    const req = isNew
      ? axios.post(`${API_BASE}/api/admin/blog`, payload, { headers: getAuthHeaders() })
      : axios.put(`${API_BASE}/api/admin/blog/${id}`, payload, { headers: getAuthHeaders() });
    req
      .then(() => {
        alert(isNew ? 'تم إنشاء المنشور' : 'تم حفظ التعديلات');
        navigate('/admin/blog');
      })
      .catch((err) => alert(err.response?.data?.error || 'فشل الحفظ'))
      .finally(() => setSaving(false));
  };

  const featuredImageUrl = form.featured_image
    ? form.featured_image.startsWith('http')
      ? form.featured_image
      : `${API_BASE}${form.featured_image}`
    : null;

  return (
    <div className="admin-blog-edit" dir="rtl">
      <div className="admin-blog-edit-header">
        <Link to="/admin/blog" className="back-link">
          <FaArrowRight /> العودة لقائمة المنشورات
        </Link>
        <h1 className="admin-page-title">{isNew ? 'منشور جديد' : 'تعديل المنشور'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-blog-form">
        <div className="form-row">
          <label>العنوان *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="عنوان المنشور"
            required
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>المقتطف (اختياري)</label>
          <textarea
            value={form.excerpt}
            onChange={(e) => handleChange('excerpt', e.target.value)}
            placeholder="نبذة قصيرة تظهر في قائمة المنشورات"
            rows={2}
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>صورة الغلاف (اختياري)</label>
          <div className="featured-image-upload">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden-input"
              id="featured-upload"
            />
            <label htmlFor="featured-upload" className="upload-label">
              <FaImage /> {uploading ? 'جاري الرفع...' : 'اختيار صورة'}
            </label>
            {featuredImageUrl && (
              <div className="featured-preview">
                <img src={featuredImageUrl} alt="غلاف" />
                <button
                  type="button"
                  className="remove-image"
                  onClick={() => handleChange('featured_image', '')}
                >
                  إزالة
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="form-row">
          <label>المحتوى</label>
          <div className="quill-wrapper">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={form.content}
              onChange={(value) => handleChange('content', value)}
              modules={modules}
              placeholder="اكتبي المحتوى هنا..."
              className="blog-quill"
            />
          </div>
        </div>

        <div className="form-row inline-row">
          <div>
            <label>الكاتب</label>
            <input
              type="text"
              value={form.author}
              onChange={(e) => handleChange('author', e.target.value)}
              className="form-input short"
            />
          </div>
          <div>
            <label>الحالة</label>
            <select
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="form-input short"
            >
              <option value="draft">مسودة</option>
              <option value="published">منشور</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </button>
          <Link to="/admin/blog" className="admin-btn-secondary">
            إلغاء
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminBlogEdit;
